import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ChevronDown, Images, ImageUp, Undo2 } from "lucide-react";
import type { AssetRef, OwnerCommand, SiteSeo } from "../api/contract";
import { PhotoError, prepareIcon, prepareShareImage } from "../lib/photos";
import { automaticPage, DESCRIPTION_SHOWN, MAX_DESCRIPTION, MAX_TITLE, TITLE_SHOWN, shownAs, siteDetailsChanges, siteDetailsForm, siteDetailsProblem, tagId, type PageDetailsForm, type SiteDetailsForm } from "../lib/siteDetails";
import { browserLanguage, browserTimeZone, languageChoices, languageName, timeZoneChoices, zoneLabel } from "../lib/siteLocale";
import type { SettingsGroup } from "../lib/siteSettings";
import { Spinner } from "./Spinner";
import { requestTimeout } from "../lib/requestTimeout";

interface SiteDetailsViewProps {
  seo: SiteSeo;
  /** The address the site is (or will be) published at, for the search preview. */
  address: string;
  /** Stores an icon upload for this site. */
  upload: (icon: Blob) => Promise<AssetRef>;
  /** The owner's photos, for "Choose from Photos". `mediaType` lets the share image leave out SVGs. */
  photos?: Array<{ id: string; label: string; src: string; mediaType?: string }>;
  /** Saves the change; resolves with whether it applied. */
  onSave: (command: Extract<OwnerCommand, { kind: "site.set" }>) => Promise<boolean>;
  /** Hands the writing to Stax in the chat. */
  onAsk: () => void;
  /**
   * The Site settings group being shown. The form keeps all three groups mounted and hides the others, so what was typed
   * in one survives a visit to another and the one Save covers them all.
   */
  group?: Extract<SettingsGroup, "search" | "language" | "analytics">;
  /** The title and icon as typed, for the modal's identity line. */
  onLive?: (live: { title: string; icon: string }) => void;
}

/** How the site shows up in browser tabs, search results and shared links. */
export function SiteDetailsView({ seo, address, upload, photos = [], onSave, onAsk, group = "search", onLive }: SiteDetailsViewProps) {
  const [form, setForm] = useState<SiteDetailsForm>(() => siteDetailsForm(seo));
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [iconStatus, setIconStatus] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const fileInput = useRef<HTMLInputElement>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageStatus, setImageStatus] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const imageInput = useRef<HTMLInputElement>(null);
  const [choosing, setChoosing] = useState<"icon" | "image" | null>(null);
  // Pages with their own details start open, so what the owner set is visible.
  const [openPages, setOpenPages] = useState(() => new Set(seo.pages.filter((page) => page.title || page.description).map((page) => page.path)));

  // Details changed elsewhere (Undo, another tab) replace the form.
  const key = JSON.stringify([seo.title, seo.description, seo.icon?.src, seo.image?.src, seo.analytics, seo.language, seo.timeZone, seo.pages]);
  useEffect(() => {
    setForm(siteDetailsForm(seo));
    setIconPreview(null);
    setImagePreview(null);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const changes = siteDetailsChanges(seo, form);
  const problem = siteDetailsProblem(form);
  const tagProblem = form.analytics && problem?.startsWith("Use a Google tag") ? problem : null;
  const title = form.title.trim() || seo.defaults.title;
  const siteDescription = form.description.trim() || seo.defaults.description || "";
  // The search result is the home page's: its own details if the owner set them, else the site's.
  const homePage = seo.pages.find((page) => page.path === "/");
  const homeAuto = homePage ? automaticPage(homePage, title, form.description.trim(), seo.fallbackDescription) : { title, description: siteDescription };
  const resultTitle = form.pages?.["/"]?.title.trim() || homeAuto.title;
  const description = form.pages?.["/"]?.description.trim() || homeAuto.description || siteDescription;
  const setPage = (path: string, patch: Partial<PageDetailsForm>) => setForm((current) => ({
    ...current, pages: { ...current.pages, [path]: { title: "", description: "", ...current.pages?.[path], ...patch } },
  }));
  const icon = form.icon === null ? seo.defaults.icon : iconPreview ?? seo.icon?.src ?? seo.defaults.icon;
  const custom = form.icon === null ? false : Boolean(form.icon || seo.icon);
  useEffect(() => { onLive?.({ title, icon }); }, [title, icon]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (file: File | undefined) => {
    if (!file || iconStatus.busy) return;
    setIconStatus({ busy: true, error: null });
    try {
      const prepared = await requestTimeout(() => prepareIcon(file), undefined, 60_000);
      const asset = await requestTimeout(() => upload(prepared), undefined, 60_000);
      setIconPreview(URL.createObjectURL(prepared));
      setForm((current) => ({ ...current, icon: asset }));
      setIconStatus({ busy: false, error: null });
    } catch (caught) {
      setIconStatus({ busy: false, error: caught instanceof PhotoError ? caught.message : "That image didn’t upload. Try again." });
    }
  };

  const shareImage = form.image === null ? null : imagePreview ?? seo.image?.src ?? null;
  const pickImage = async (file: File | undefined) => {
    if (!file || imageStatus.busy) return;
    setImageStatus({ busy: true, error: null });
    try {
      const prepared = await requestTimeout(() => prepareShareImage(file), undefined, 60_000);
      const asset = await requestTimeout(() => upload(prepared), undefined, 60_000);
      setImagePreview(URL.createObjectURL(prepared));
      setForm((current) => ({ ...current, image: asset }));
      setImageStatus({ busy: false, error: null });
    } catch (caught) {
      setImageStatus({ busy: false, error: caught instanceof PhotoError ? caught.message : "That image didn’t upload. Try again." });
    }
  };

  const choosePhoto = (photo: { id: string; src: string }) => {
    if (choosing === "icon") { setIconPreview(photo.src); setForm((current) => ({ ...current, icon: { photoId: photo.id } })); setIconStatus({ busy: false, error: null }); }
    else { setImagePreview(photo.src); setForm((current) => ({ ...current, image: { photoId: photo.id } })); setImageStatus({ busy: false, error: null }); }
    setChoosing(null);
  };
  const choices = choosing === "image" ? photos.filter((photo) => photo.mediaType !== "image/svg+xml") : photos;
  const picker = (which: "icon" | "image") => choosing === which && (
    <div className="bw-photo-picker" role="group" aria-label={which === "icon" ? "Choose an icon from your photos" : "Choose a share image from your photos"}>
      {choices.length === 0 ? <p className="bw-item-note">{which === "image" && photos.length > 0 ? "Your photos are all SVGs, which link previews can’t show." : "You don’t have any photos yet."}</p> : (
        <div className="bw-photo-grid">
          {choices.map((photo) => (
            <button key={photo.id} type="button" className="bw-photo-option" onClick={() => choosePhoto(photo)}>
              <img src={photo.src} alt="" />
              <span>{photo.label}</span>
            </button>
          ))}
        </div>
      )}
      <button type="button" className="bw-btn bw-btn--text" onClick={() => setChoosing(null)}>Cancel</button>
    </div>
  );
  const chooseButton = (which: "icon" | "image") => (
    <button type="button" className="bw-btn bw-btn--quiet" disabled={which === "icon" ? iconStatus.busy : imageStatus.busy} aria-expanded={choosing === which} onClick={() => setChoosing(choosing === which ? null : which)}>
      <Images size={16} aria-hidden="true" /> Choose from Photos
    </button>
  );

  const languages = useMemo(() => {
    const list = languageChoices(seo.language);
    // A browser language the list doesn't have is still a choice once picked.
    if (form.language && !list.some((choice) => choice.tag === form.language)) list.push({ tag: form.language, label: languageName(form.language) });
    return list;
  }, [form.language, seo.language]);
  const zones = useMemo(() => {
    const all = timeZoneChoices(seo.timeZone);
    if (form.timeZone && !all.includes(form.timeZone)) all.push(form.timeZone);
    const groups = new Map<string, string[]>();
    for (const zone of all) {
      if (zone === seo.timeZone) continue;
      const area = zone.includes("/") ? zone.split("/")[0]! : "Other";
      groups.set(area, [...(groups.get(area) ?? []), zone]);
    }
    return { current: seo.timeZone ?? null, groups: [...groups.entries()] };
  }, [form.timeZone, seo.timeZone]);
  const suggestedLanguage = useMemo(() => browserLanguage(), []);
  const suggestedZone = useMemo(() => browserTimeZone(), []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!changes || problem || status === "saving") return;
    setStatus("saving");
    try { setStatus(await requestTimeout(() => onSave(changes), undefined, 60_000) ? "saved" : "failed"); }
    catch { setStatus("failed"); }
  };

  return (
    <form className="bw-brand bw-site-details" aria-busy={status === "saving" || iconStatus.busy || imageStatus.busy} onSubmit={(event) => void submit(event)}>
      <div className="bw-settings-part" hidden={group !== "search"}>
        <figure className="bw-serp" aria-label="Search result preview">
          <div className="bw-serp-site">
            <img src={icon} alt="" width={28} height={28} />
            <span>
              <strong>{shownAs(title, 40)}</strong>
              <small>{address}</small>
            </span>
          </div>
          <p className="bw-serp-title">{shownAs(resultTitle, TITLE_SHOWN)}</p>
          {description ? <p className="bw-serp-text">{shownAs(description, DESCRIPTION_SHOWN)}</p> : <p className="bw-serp-text" data-empty>Add a description so search results say what you do.</p>}
        </figure>

        <section aria-labelledby="bw-details-text">
          <h3 id="bw-details-text" className="bw-library-title">Title and description</h3>
          <div className="bw-item-edit">
            <label>
              <span>Site title</span>
              <input value={form.title} placeholder={seo.defaults.title} maxLength={MAX_TITLE + 20} aria-invalid={form.title.trim().length > MAX_TITLE || undefined} onChange={(event) => setForm({ ...form, title: event.target.value })} />
              <small className="bw-count" data-over={form.title.trim().length > TITLE_SHOWN || undefined}>{form.title.trim().length}/{TITLE_SHOWN}</small>
            </label>
            <label>
              <span>Description</span>
              <textarea rows={3} value={form.description} placeholder={seo.defaults.description ?? "One or two sentences about what you offer and who it’s for."} maxLength={MAX_DESCRIPTION + 40} aria-invalid={form.description.trim().length > MAX_DESCRIPTION || undefined} onChange={(event) => setForm({ ...form, description: event.target.value })} />
              <small className="bw-count" data-over={form.description.trim().length > DESCRIPTION_SHOWN || undefined}>{form.description.trim().length}/{DESCRIPTION_SHOWN}</small>
            </label>
            <p className="bw-item-note">Leave a field empty to use what your website says. Other pages add their own name, like “{seo.pages.find((page) => page.label)?.label ?? "About"} · {shownAs(title, 40)}”.</p>
            {problem && !tagProblem && <p className="bw-item-note" role="alert">{problem}</p>}
          </div>
        </section>

        <section aria-labelledby="bw-details-icon">
          <h3 id="bw-details-icon" className="bw-library-title">Browser tab icon</h3>
          <div className="bw-icon-row">
            <img className="bw-icon-preview" src={icon} alt={custom ? "Your icon" : "Your initial on your brand colour"} width={56} height={56} />
            <div>
              <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={(event) => { void pick(event.target.files?.[0]); event.target.value = ""; }} />
              <div className="bw-actions">
                <button type="button" className="bw-btn bw-btn--quiet" disabled={iconStatus.busy} onClick={() => fileInput.current?.click()}>
                  {iconStatus.busy ? <Spinner /> : <ImageUp size={16} aria-hidden="true" />} {custom ? "Change icon" : "Upload icon"}
                </button>
                {chooseButton("icon")}
                {custom && (
                  <button type="button" className="bw-btn bw-btn--text" onClick={() => { setIconPreview(null); setForm({ ...form, icon: seo.icon ? null : undefined }); }}>
                    <Undo2 size={16} aria-hidden="true" /> Use my initial
                  </button>
                )}
              </div>
              {picker("icon")}
              <p className="bw-item-note">A square image works best, like your logo. A PNG, JPEG or WebP is cropped to its middle; an SVG is used as it is.</p>
              {iconStatus.error && <p className="bw-item-note" role="alert">{iconStatus.error}</p>}
            </div>
          </div>
        </section>

        <section aria-labelledby="bw-details-share">
          <h3 id="bw-details-share" className="bw-library-title">Share image</h3>
          <div className="bw-share-row">
            <div className="bw-share-preview" data-empty={shareImage ? undefined : true}>
              {shareImage ? <img src={shareImage} alt="How your share image looks in a link preview" /> : <span>Each page uses its own photo, or your icon</span>}
            </div>
            <input ref={imageInput} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(event) => { void pickImage(event.target.files?.[0]); event.target.value = ""; }} />
            <div className="bw-actions">
              <button type="button" className="bw-btn bw-btn--quiet" disabled={imageStatus.busy} onClick={() => imageInput.current?.click()}>
                {imageStatus.busy ? <Spinner /> : <ImageUp size={16} aria-hidden="true" />} {shareImage ? "Change image" : "Choose image"}
              </button>
              {chooseButton("image")}
              {shareImage && (
                <button type="button" className="bw-btn bw-btn--text" onClick={() => { setImagePreview(null); setForm({ ...form, image: seo.image ? null : undefined }); }}>
                  <Undo2 size={16} aria-hidden="true" /> Use each page’s photo
                </button>
              )}
            </div>
            {picker("image")}
            <p className="bw-item-note">Shown when a page is shared in a message or on social media. Wide photos work best; we use the middle of it.</p>
            {imageStatus.error && <p className="bw-item-note" role="alert">{imageStatus.error}</p>}
          </div>
        </section>

        {seo.pages.length > 0 && (
          <section aria-label="Search fields for each page">
            <details className="bw-page-group">
              <summary>
                <span>Pages on your Website ({seo.pages.length})</span>
                <ChevronDown size={16} aria-hidden="true" />
              </summary>
              <p className="bw-item-note">Give a page its own title and description in search results. Empty fields stay automatic.</p>
              <ul className="bw-page-meta">
                {seo.pages.map((page) => {
                  const auto = automaticPage(page, title, form.description.trim(), seo.fallbackDescription);
                  const edited = form.pages?.[page.path] ?? { title: "", description: "" };
                  const custom = Boolean(edited.title.trim() || edited.description.trim());
                  return (
                    <li key={page.path}>
                      <details open={openPages.has(page.path)} onToggle={(event) => {
                        const open = event.currentTarget.open;
                        setOpenPages((current) => { const next = new Set(current); if (open) next.add(page.path); else next.delete(page.path); return next; });
                      }}>
                        <summary>
                          <span>
                            <strong>{page.path === "/" ? "Home" : page.label ?? page.path}</strong>
                            <small>{shownAs(edited.title.trim() || auto.title, TITLE_SHOWN)}</small>
                          </span>
                          {custom && <em>Custom</em>}
                          <ChevronDown size={16} aria-hidden="true" />
                        </summary>
                        <div className="bw-item-edit">
                          <label>
                            <span>Page title</span>
                            <input value={edited.title} placeholder={auto.title} maxLength={MAX_TITLE + 20} aria-invalid={edited.title.trim().length > MAX_TITLE || undefined} onChange={(event) => setPage(page.path, { title: event.target.value })} />
                            <small className="bw-count" data-over={edited.title.trim().length > TITLE_SHOWN || undefined}>{edited.title.trim().length}/{TITLE_SHOWN}</small>
                          </label>
                          <label>
                            <span>Page description</span>
                            <textarea rows={2} value={edited.description} placeholder={auto.description || "One or two sentences about this page."} maxLength={MAX_DESCRIPTION + 40} aria-invalid={edited.description.trim().length > MAX_DESCRIPTION || undefined} onChange={(event) => setPage(page.path, { description: event.target.value })} />
                            <small className="bw-count" data-over={edited.description.trim().length > DESCRIPTION_SHOWN || undefined}>{edited.description.trim().length}/{DESCRIPTION_SHOWN}</small>
                          </label>
                        </div>
                      </details>
                    </li>
                  );
                })}
              </ul>
            </details>
            <p className="bw-item-note">Posts and products have their own search fields.</p>
          </section>
        )}
      </div>

      <div className="bw-settings-part" hidden={group !== "language"}>
        <div className="bw-item-edit">
          <label>
            <span>Language</span>
            <select value={form.language ?? ""} onChange={(event) => setForm({ ...form, language: event.target.value })}>
              <option value="">Not set (your design’s own)</option>
              {languages.map((choice) => <option key={choice.tag} value={choice.tag}>{choice.label}</option>)}
            </select>
          </label>
          {!form.language && suggestedLanguage && (
            <p className="bw-item-note">
              Your browser uses {languageName(suggestedLanguage)}.{" "}
              <button type="button" className="bw-btn bw-btn--text" onClick={() => setForm({ ...form, language: suggestedLanguage })}>Use {languageName(suggestedLanguage)}</button>
            </p>
          )}
          <label>
            <span>Time zone</span>
            <select value={form.timeZone ?? ""} onChange={(event) => setForm({ ...form, timeZone: event.target.value })}>
              <option value="">Not set (dates show the UTC day)</option>
              {zones.current && <option value={zones.current}>{zones.current.replace(/_/g, " ")}</option>}
              {zones.groups.map(([area, list]) => (
                <optgroup key={area} label={area}>
                  {list.map((zone) => <option key={zone} value={zone}>{zone.slice(area.length + 1).replace(/_/g, " ") || zone}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          {!form.timeZone && suggestedZone && (
            <p className="bw-item-note">
              Your browser is on {suggestedZone.replace(/_/g, " ")}.{" "}
              <button type="button" className="bw-btn bw-btn--text" onClick={() => setForm({ ...form, timeZone: suggestedZone })}>Use {suggestedZone.split("/").pop()!.replace(/_/g, " ")}</button>
            </p>
          )}
          <p className="bw-item-note">
            Used for blog post dates and scheduled posts.{" "}
            {form.timeZone ? `Scheduled posts go out on ${zoneLabel(form.timeZone)}. ` : "Without a time zone, scheduled posts use your browser’s. "}
            The language sets how dates read and what search engines and screen readers are told.
          </p>
        </div>
      </div>

      <div className="bw-settings-part" hidden={group !== "analytics"}>
        <div className="bw-item-edit">
          <label>
            <span>Google tag ID</span>
            <input value={form.analytics ?? ""} placeholder="G-ABC123XYZ" autoCapitalize="characters" autoComplete="off" spellCheck={false} maxLength={30}
              aria-invalid={Boolean(form.analytics && tagProblem) || undefined} onChange={(event) => setForm({ ...form, analytics: tagId(event.target.value) })} />
          </label>
          <p className="bw-item-note" role={tagProblem ? "alert" : undefined}>
            {tagProblem ?? "Find it in Google Analytics under Admin, Data streams. It counts visits to your published website, not previews."}
          </p>
        </div>
      </div>

      <div className="bw-brand-save">
        {status === "saved" && !changes && (
          <div className="bw-callout" data-tone="done" role="status">
            <p>Saved to your draft. It shows on your live site after you publish.</p>
          </div>
        )}
        {status === "failed" && <p className="bw-item-note" role="alert">That didn’t save. Try again.</p>}
        <div className="bw-actions">
          <button type="submit" className="bw-btn bw-btn--primary" disabled={!changes || Boolean(problem) || iconStatus.busy || imageStatus.busy || status === "saving"}>
            {status === "saving" && <Spinner />}
            {status === "saving" ? "Saving…" : "Save"}
          </button>
          {group === "search" && <button type="button" className="bw-btn bw-btn--text" onClick={onAsk}>Ask Stax to write them</button>}
        </div>
      </div>
    </form>
  );
}
